import { useContext, useEffect, useMemo, useState } from "react";
import { createSearchParams, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Grid } from "@mui/material";
import { toast } from "react-toastify";
import axios from "axios";
import BASE_URL from "../../../constants/baseUrls";
import { useTranslation } from "react-i18next";
import { LocaleContext } from "../../../routing/LangRouter";
import { convertToJalali } from "../../../utils/date";
import { IS_MOBILE } from "../../../constants/breakpoints";
import { navigateToLoginPage } from "../../../utils/utility";

//import general components
import { BaseButton } from "../../../components/common/base-button/base-button";
import BaseTable from "../../../components/common/base-table/base-table";
import BaseTextField from "../../../components/common/base-text-field/base-text-field";
import QuestionModal from "../../../components/common/question-modal/question-modal";
import BasePagination from "../../../components/common/base-pagination/base-pagination";
import BaseTitle from "../../../components/common/base-title/base-title";

//import view components
import AddPassengerModal from "../../../components/view/user-panel/passenger/add-passenger";
import PassengerList from "../../../components/view/user-panel/passenger/mobile-passenger-list";
import PassengerDetails from "../../../components/view/user-panel/passenger/passenger-details";

//import images and icons
import Icon from "../../../components/common/base-icon/icon";
import { ProfileAdd, Trash, User, UserEdit } from "iconsax-react";

//import styles
import "./passenger.scss";

function Passenger() {
    //general
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const { t } = useTranslation();
    const { locale } = useContext(LocaleContext);
    const [loading, setLoading] = useState(false);
    const accessToken = localStorage.getItem("accessToken");

    const [selectedPassenger, setSelectedPassenger] = useState(false);
    const [openDetails, setOpenDetails] = useState(false);
    const handleSelectedPassenger = (passenger, isOpen) => {
        setSelectedPassenger(passenger);
        setOpenDetails(isOpen);
    };

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1); // Active page
    const [totalPages, setTotalPages] = useState(1); // Total pages from API
    const [searchValue, setSearchValue] = useState(""); // Search query

    //request to get passenger list
    const [passengerList, setPassengerList] = useState([]);
    const [searchedPassengerList, setSearchedPassengerList] = useState([]);
    const getPassengerList = async (page = 1, search = "") => {
        try {
            setLoading(true);
            const response = await axios.get(`${BASE_URL}/api/v2/account/user/passenger/`, {
                params: {
                    pk: "",
                    page,
                    search,
                },
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    "Content-Type": "application/json",
                },
            });
            const { results, total_pages, current_page } = response.data.data;
            setPassengerList(results);
            setSearchedPassengerList(results);
            setCurrentPage(current_page); // Update current page state
            setTotalPages(total_pages); // Update total pages state
        } catch (error) {
            if (error.response.status === 401) {
                localStorage.clear();
                navigateToLoginPage(navigate, locale, location);
            } else {
                toast.error(t("Toast Messages.Error Occurred"), { toastId: "custom-id-yes" });
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getPassengerList(currentPage, searchValue);
    }, [currentPage, searchValue]);

    // Handle search value change
    const handleSearchChange = event => {
        const value = event.target.value;
        setSearchValue(value); // Update search value
        setCurrentPage(1); // Reset to first page for new search
    };

    //table's columns
    const columns = useMemo(() => [
        {
            accessorKey: "gender",
            header: " ",
            enableSorting: false,
            size: 30,
        },
        {
            accessorKey: "name",
            header: `${t("firstName")} ${t("lastName")}`,
            enableSorting: false,
        },
        {
            accessorKey: "nationalCode",
            header: `${t("nationalCode")}`,
            enableSorting: false,
        },
        {
            accessorKey: "birthdate",
            header: `${t("birthday")}`,
            enableSorting: false,
        },
        {
            accessorKey: "passportNumber",
            header: `${t("passportNumber")}`,
            enableSorting: false,
        },
        {
            accessorKey: "edit",
            header: "",
            enableSorting: false,
            size: 50,
        },
        {
            accessorKey: "delete",
            header: "",
            enableSorting: false,
            size: 50,
        },
    ]);

    //table's rows
    const [rows, setRows] = useState([]);
    useEffect(() => {
        if (!!passengerList?.length) {
            setRows(
                passengerList?.map(passengerItem => ({
                    gender:
                        passengerItem?.gender.id === 1 ? (
                            <User color="var(--teal-color-18)" variant="Bold" />
                        ) : passengerItem?.gender.id === 2 ? (
                            <Icon name="genderIcon" width="1rem" />
                        ) : (
                            "-"
                        ),
                    name: (
                        <Grid container item xs="auto" direction="column">
                            <span>{`${passengerItem?.first_name || " "} ${passengerItem?.last_name || " "}`}</span>
                            <span>
                                {passengerItem?.english_first_name} {passengerItem?.english_last_name}
                            </span>
                        </Grid>
                    ),
                    nationalCode: passengerItem?.national_code,
                    birthdate: locale === "en" ? passengerItem?.birthdate : convertToJalali(passengerItem?.birthdate),
                    passportNumber: (
                        <Grid container item xs="auto" direction="column">
                            <span>{passengerItem?.passport_no}</span>
                            <span>{passengerItem?.passport_expiry_date?.split("-").join("/")}</span>
                        </Grid>
                    ),
                    edit: (
                        <BaseButton
                            color="transparent"
                            size="large"
                            radius="radius-10"
                            type="submit"
                            rightIcon={<UserEdit color="var(--primary-color)" />}
                            handleClick={() => actionOnPassenger("edit", passengerItem?.id)}
                        />
                    ),
                    delete: (
                        <BaseButton
                            color="transparent"
                            size="large"
                            radius="radius-10"
                            type="submit"
                            rightIcon={<Trash color="var(--red-color)" />}
                            handleClick={() => actionOnPassenger("delete", passengerItem?.id)}
                        />
                    ),
                })),
            );
        }
    }, [passengerList]);

    //pagination logic
    const handlePageChange = (event, page) => {
        setCurrentPage(page); // Update page state
    };

    //modal
    const [isOpenForm, setIsOpenForm] = useState(false);
    const [isOpenDialog, setIsOpenDialog] = useState(false);
    const actionOnPassenger = (action, id) => {
        if (action === "add" || action === "edit") {
            setIsOpenForm(true);
        }

        if (action === "delete") {
            setIsOpenDialog(true);
        }
        setSearchParams({ action, id });
    };

    useEffect(() => {
        if (searchParams.size === 0) {
            setIsOpenForm(false);
            setIsOpenDialog(false);
        }
    }, [searchParams]);

    //request to delete a passenger
    const deletePassenger = async () => {
        try {
            const response = await axios.delete(`${BASE_URL}/api/v2/account/user/passenger/`, {
                params: {
                    pk: searchParams.get("id"),
                },
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });
            toast.success(response.data.message, { toastId: "custom-id-yes" });
            getPassengerList();
        } catch (error) {
            if (error.response.status === 401) {
                localStorage.clear();
                navigate({
                    pathname: `/${locale}/authentication/signin`,
                    search: `?${createSearchParams({ prevLocation: location.pathname })}`,
                });
                toast.error(t("Toast Messages.Re-login"), { toastId: "custom-id-yes" });
            } else {
                toast.error(t("Toast Messages.Error Occurred"), { toastId: "custom-id-yes" });
            }
        }
        setIsOpenDialog(false);
    };

    return (
        <>
            <Grid className="passenger" container item xs={12} rowGap={2} mt={{ xs: 2, md: 0 }} mb={{ xs: 8, md: 5 }}>
                {!IS_MOBILE && <BaseTitle title={`${t("passengers2")}`} dividerColor="1px solid var(--medium-gray-color)" />}

                <Grid className="passenger__table" container item xs={12} rowGap={{ xs: 1, md: 0 }}>
                    <Grid className="passenger__table-header" container item xs={12} justifyContent="space-between">
                        <Grid container item md={8}>
                            <BaseTextField
                                label={t("enterNameLabel")}
                                size={IS_MOBILE ? "small" : "medium"}
                                radius="10px"
                                value={searchValue}
                                setValue={handleSearchChange}
                            />
                        </Grid>
                        <Grid container item xs={1.5} display={{ xs: "none", md: "flex" }}>
                            <BaseButton text={t("search")} color="teal-8" radius="radius-10" handleClick={() => passengerList(searchValue)} />
                        </Grid>
                        <Grid className="passenger__add-passenger" container item md={2}>
                            <BaseButton
                                text={t("addFellowTraveler")}
                                color="primary"
                                size={IS_MOBILE ? "medium" : ""}
                                radius="radius-10"
                                rightIcon={<ProfileAdd color="var(--light-color)" />}
                                handleClick={() => actionOnPassenger("add", 0)}
                            />
                        </Grid>
                    </Grid>
                    {IS_MOBILE ? (
                        <Grid container item xs={12} rowGap={1}>
                            {searchedPassengerList?.map(passengerItem => {
                                return (
                                    <Grid container item xs={12} key={passengerItem.id} rowGap={1}>
                                        <PassengerList passengerItem={passengerItem} handleSelectedPassenger={handleSelectedPassenger} />
                                    </Grid>
                                );
                            })}
                        </Grid>
                    ) : (
                        <Grid container item xs={12}>
                            <BaseTable
                                columns={columns}
                                rows={rows}
                                details={false}
                                action={false}
                                loading={loading}
                                rowBackground="var(--light-color)"
                            />
                        </Grid>
                    )}
                    <Grid container item xs={12} justifyContent="center">
                        <BasePagination count={totalPages} page={currentPage ?? 1} handleChange={handlePageChange} />
                    </Grid>
                </Grid>
            </Grid>

            <PassengerDetails open={openDetails} setOpen={setOpenDetails} passenger={selectedPassenger} actionOnPassenger={actionOnPassenger} />
            <AddPassengerModal getPassengerList={getPassengerList} open={isOpenForm} setOpen={setIsOpenForm} />
            <QuestionModal
                open={isOpenDialog}
                setOpen={setIsOpenDialog}
                question={t("Messages.Confirm Companion Removal")}
                colorYes="blue-gray"
                colorNo="teal-18"
                handleClick={deletePassenger}
            />
        </>
    );
}

export default Passenger;
